/**
 * Página de Pagamento - Plano PRO
 * 
 * Interface para contratação e gerenciamento de assinatura
 * 
 * @author Sophya Sistema
 * @date 12/11/2025
 */

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { MercadoPagoPaymentBrick } from '../components/MercadoPagoPaymentBrick';
import trackingService from '../services/trackingService';

interface PlanoData {
  id: number;
  nome: string;
  descricao: string;
  valor_mensal: number;
  valor_anual: number;
  recursos: {
    pacientes_ilimitados: boolean;
    usuarios_ilimitados: boolean;
    armazenamento_ilimitado: boolean;
    google_calendar: boolean;
    email_automatico: boolean;
    prontuarios_digitais: boolean;
    relatorios_completos: boolean;
    suporte_prioritario: boolean;
  };
  dias_teste: number;
}

interface AssinaturaData {
  id: number;
  plano_id: number;
  plano_nome: string;
  status_assinatura: 'teste' | 'ativo' | 'expirado' | 'cancelado' | 'inadimplente';
  // Data de expiração efetiva calculada pela view (teste ou plano ativo)
  data_expiracao_efetiva?: string;
  // Dias restantes já calculados pela view (fallback calculado se ausente)
  dias_restantes?: number;
}

interface TransacaoData {
  id: number;
  mp_payment_id: string;
  plano_id: number;
  frequencia: 'mensal' | 'anual';
  valor: number;
  status: string;
  status_detalhe: string;
  metodo_pagamento: string;
  tipo_pagamento: string;
  descricao: string;
  data_criacao: string;
  data_aprovacao?: string;
}

export const PagamentoPage: React.FC = () => {
  
  // Estados principais
  const [plano, setPlano] = useState<PlanoData | null>(null);
  const [assinatura, setAssinatura] = useState<AssinaturaData | null>(null);
  const [frequencia, setFrequencia] = useState<'mensal' | 'anual'>('mensal');
  const [mostrarBrick, setMostrarBrick] = useState(false);
  
  // ✅ NOVO: Estados de cupom
  const [codigoCupom, setCodigoCupom] = useState('');
  const [cupomAplicado, setCupomAplicado] = useState<{
    codigo: string;
    desconto_percentual: number;
  } | null>(null);
  const [validandoCupom, setValidandoCupom] = useState(false);
  const [erroCupom, setErroCupom] = useState('');
  
  // Estados de feedback
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  
  // ✅ NOVO: Estado de transações
  const [transacoes, setTransacoes] = useState<TransacaoData[]>([]);

  // Carregar dados ao montar
  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      
      // Buscar plano PRO
      const resPlano = await api.get('/mercadopago/planos');
      if (resPlano.data.sucesso) {
        setPlano(resPlano.data.plano);
      }
      
      // Buscar assinatura atual
      const resAssinatura = await api.get('/mercadopago/assinatura');
      if (resAssinatura.data.sucesso) {
        setAssinatura(resAssinatura.data.assinatura);
      }
      
      // ✅ NOVO: Buscar transações
      await carregarTransacoes();
      
    } catch (error: any) {
      console.error('[PAGAMENTO] Erro ao carregar dados:', error);
      if (error.response?.status !== 404) {
        setErro('Erro ao carregar informações');
      }
    } finally {
      setLoading(false);
    }
  };

  const carregarTransacoes = async () => {
    try {
      const response = await api.get('/mercadopago/transacoes');
      if (response.data.sucesso) {
        setTransacoes(response.data.transacoes || []);
      }
    } catch (error: any) {
      console.error('[PAGAMENTO] Erro ao carregar transações:', error);
      // Não exibir erro se for 404 (sem transações)
      if (error.response?.status !== 404) {
        // Silencioso - transações são opcionais
      }
    }
  };

  // ✅ NOVO: Validar e aplicar cupom
  const handleValidarCupom = async () => {
    if (!codigoCupom.trim()) {
      setErroCupom('Digite um código de cupom');
      return;
    }
    
    try {
      setValidandoCupom(true);
      setErroCupom('');
      
      // ✅ CRÍTICO: Garantir que valor seja NUMBER, não string
      const valorOriginal = frequencia === 'anual' ? plano!.valor_anual : plano!.valor_mensal;
      const valorNumerico = typeof valorOriginal === 'string' 
        ? parseFloat(valorOriginal) 
        : valorOriginal;
      
      console.log('[PAGAMENTO] ═══════════════════════════════════════');
      console.log('[PAGAMENTO] Validando cupom');
      console.log('[PAGAMENTO] Dados:', {
        codigo: codigoCupom,
        tipo_plano: frequencia,
        valor_original: valorNumerico
      });
      console.log('[PAGAMENTO] Tipos:', {
        codigo: typeof codigoCupom,
        tipo_plano: typeof frequencia,
        valor_original: typeof valorNumerico
      });
      
      const response = await api.post('/cupons/calcular-desconto', {
        codigo: codigoCupom,
        tipo_plano: frequencia,
        valor_original: valorNumerico // ✅ Usar valor numérico convertido
      });
      
      console.log('[PAGAMENTO] Response:', response.data);
      
      if (response.data.sucesso) {
        console.log('[PAGAMENTO] ✅ Cupom válido!');
        console.log('[PAGAMENTO] ═══════════════════════════════════════');
        setCupomAplicado({
          codigo: response.data.cupom.codigo,
          desconto_percentual: response.data.cupom.desconto_percentual
        });
        setErroCupom('');
        alert(`✅ Cupom aplicado! Desconto de ${response.data.cupom.desconto_percentual}%`);
      }
    } catch (error: any) {
      console.error('[PAGAMENTO] ═══════════════════════════════════════');
      console.error('[PAGAMENTO] ❌ Erro ao validar cupom');
      console.error('[PAGAMENTO] Error completo:', error);
      console.error('[PAGAMENTO] Response:', error.response);
      console.error('[PAGAMENTO] Response data:', error.response?.data);
      console.error('[PAGAMENTO] Response status:', error.response?.status);
      console.error('[PAGAMENTO] ═══════════════════════════════════════');
      setErroCupom(error.response?.data?.mensagem || 'Cupom inválido');
      setCupomAplicado(null);
    } finally {
      setValidandoCupom(false);
    }
  };

  const handleRemoverCupom = () => {
    setCupomAplicado(null);
    setCodigoCupom('');
    setErroCupom('');
  };

  const calcularValorComDesconto = (): number => {
    if (!plano) return 0;
    
    const valorBase = frequencia === 'anual' ? plano.valor_anual : plano.valor_mensal;
    
    if (cupomAplicado) {
      const desconto = (valorBase * cupomAplicado.desconto_percentual) / 100;
      return valorBase - desconto;
    }
    
    return valorBase;
  };

  const handleIniciarPagamento = () => {
    setErro('');
    setMostrarBrick(true);
    
    // Rastrear início de checkout
    if (plano) {
      const valorSelecionado = frequencia === 'anual' ? plano.valor_anual : plano.valor_mensal;
      trackingService.trackPaymentStarted({
        planName: plano.nome,
        planId: String(plano.id),
        value: valorSelecionado,
        billingPeriod: frequencia
      });
    }
  };

  const handleSuccess = async (paymentId: number, status: string, paymentData?: any) => {
    console.log('[PAGAMENTO] Pagamento aprovado:', { paymentId, status, paymentData });
    
    // Rastrear assinatura completa
    if (plano) {
      const valorSelecionado = frequencia === 'anual' ? plano.valor_anual : plano.valor_mensal;
      trackingService.trackSubscriptionComplete({
        planName: plano.nome,
        planId: String(plano.id),
        value: valorSelecionado,
        billingPeriod: frequencia,
        orderId: String(paymentId)
      });
    }
    
    try {
      // Salvar transação no backend
      const valorSelecionado = frequencia === 'anual' 
        ? plano!.valor_anual 
        : plano!.valor_mensal;
      
      await api.post('/mercadopago/payment/plano', {
        plano_id: plano!.id,
        frequencia,
        mp_payment_id: String(paymentId), // Garantir que seja string
        valor: valorSelecionado,
        status: status,
        metodo_pagamento: paymentData?.payment_method_id || null,
        tipo_pagamento: paymentData?.payment_type_id || null,
        status_detalhe: paymentData?.status_detail || null,
        pagador_nome: paymentData?.payer?.first_name || null,
        pagador_email: paymentData?.payer?.email || null,
        descricao: `Plano ${plano!.nome} - ${frequencia === 'anual' ? 'Anual' : 'Mensal'}`,
        parcelas: paymentData?.installments || 1,
        data_criacao: paymentData?.date_created || new Date().toISOString(),
        data_aprovacao: paymentData?.date_approved || null,
        response_json: paymentData || { paymentId, status },
        cupom_codigo: cupomAplicado?.codigo || null // ✅ NOVO: Enviar código do cupom
      });
      
      setMostrarBrick(false);
      
      // Recarregar dados (incluindo transações)
      await carregarDados();
      
    } catch (error: any) {
      console.error('[PAGAMENTO] Erro ao salvar transação:', error);
      setErro('Pagamento aprovado, mas erro ao registrar. Contate o suporte.');
    }
  };

  const handleError = (error: any) => {
    console.error('[PAGAMENTO] Erro no pagamento:', error);
    setErro('Erro ao processar pagamento. Tente novamente.');
    setMostrarBrick(false);
  };

  const calcularDesconto = (): number => {
    if (!plano) return 0;
    const totalMensal = plano.valor_mensal * 12;
    return Math.round(((totalMensal - plano.valor_anual) / totalMensal) * 100);
  };

  const formatarValor = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const calcularDiasRestantes = (): number | null => {
    if (!assinatura) return null;
    // Usar diretamente dias_restantes da view se presente
    if (typeof assinatura.dias_restantes === 'number') {
      return assinatura.dias_restantes;
    }
    // Fallback: calcular a partir de data_expiracao_efetiva
    if (assinatura.data_expiracao_efetiva) {
      const agora = new Date();
      const dataExp = new Date(assinatura.data_expiracao_efetiva);
      const diff = dataExp.getTime() - agora.getTime();
      const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
      return Math.max(0, dias);
    }
    return null;
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'teste':
        return 'badge bg-info';
      case 'ativo':
        return 'badge bg-success';
      case 'expirado':
        return 'badge bg-danger';
      case 'cancelado':
        return 'badge bg-secondary';
      case 'inadimplente':
        return 'badge bg-warning';
      default:
        return 'badge bg-secondary';
    }
  };

  const getStatusTexto = (status: string): string => {
    switch (status) {
      case 'teste':
        return 'Período de Teste';
      case 'ativo':
        return 'Ativo';
      case 'expirado':
        return 'Expirado';
      case 'cancelado':
        return 'Cancelado';
      case 'inadimplente':
        return 'Inadimplente';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <section className="content">
        <div className="container-fluid">
          <div className="row justify-content-center py-5">
            <div className="col-md-6 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <p className="mt-3">Carregando informações...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-12">
              <h1>
                <i className="fas fa-credit-card text-primary"></i> Pagamento - Plano PRO
              </h1>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          
          {/* Mensagens de Erro */}
          {erro && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {erro}
              <button type="button" className="btn-close" onClick={() => setErro('')}></button>
            </div>
          )}

          {/* Status da Assinatura */}
          {assinatura && (
            <div className="row justify-content-center mb-4">
              <div className="col-12 col-md-10 col-lg-8 col-xl-6 mx-auto">
                <div className="card shadow-sm">
                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="fas fa-info-circle me-2"></i>
                      Status da Assinatura
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <p className="mb-2">
                          <strong>Plano:</strong> {assinatura.plano_nome}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <p className="mb-2">
                          <strong>Status:</strong>{' '}
                          <span className={getStatusBadgeClass(assinatura.status_assinatura)}>
                            {getStatusTexto(assinatura.status_assinatura)}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-4">
                        {(() => {
                          const dias = calcularDiasRestantes();
                          if (dias !== null) {
                            return (
                              <p className="mb-2">
                                <strong>Dias Restantes:</strong>{' '}
                                <span className={dias <= 7 ? 'text-danger' : 'text-success'}>
                                  {dias} {dias === 1 ? 'dia' : 'dias'}
                                </span>
                              </p>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Card do Plano PRO */}
          {plano && (
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 col-xl-6 mx-auto">
                <div className="card card-primary card-outline shadow-sm">
                  <div className="card-header text-center">
                    <h3 className="card-title">
                      <i className="fas fa-crown me-2 text-warning"></i>
                      Plano {plano.nome}
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="text-center text-muted small mb-3">
                      <i className="fas fa-lock me-1"></i>
                      Pagamento 100% seguro via Mercado Pago
                    </div>
                    
                    {/* Toggle Mensal/Anual */}
                    <div className="text-center mb-4">
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className={`btn ${frequencia === 'mensal' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setFrequencia('mensal')}
                        >
                          Mensal
                        </button>
                        <button
                          type="button"
                          className={`btn ${frequencia === 'anual' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setFrequencia('anual')}
                        >
                          Anual
                          {calcularDesconto() > 0 && (
                            <span className="badge bg-success ms-2">
                              -{calcularDesconto()}%
                            </span>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* ✅ NOVO: Campo de Cupom de Desconto */}
                    <div className="mb-4">
                      <div className="card bg-light">
                        <div className="card-body p-3">
                          <h6 className="card-title mb-3">
                            <i className="fas fa-ticket-alt me-2 text-success"></i>
                            Cupom de Desconto
                          </h6>
                          
                          {!cupomAplicado ? (
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Digite o código do cupom"
                                value={codigoCupom}
                                onChange={(e) => setCodigoCupom(e.target.value.toUpperCase())}
                                disabled={validandoCupom}
                              />
                              <button
                                className="btn btn-success"
                                type="button"
                                onClick={handleValidarCupom}
                                disabled={!codigoCupom || validandoCupom}
                              >
                                {validandoCupom ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Validando...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-check me-2"></i>
                                    Aplicar
                                  </>
                                )}
                              </button>
                            </div>
                          ) : (
                            <div className="alert alert-success alert-dismissible fade show mb-0 py-2" role="alert">
                              <i className="fas fa-check-circle me-2"></i>
                              <strong>{cupomAplicado.codigo}</strong> aplicado ({cupomAplicado.desconto_percentual}% de desconto)
                              <button
                                type="button"
                                className="btn-close btn-close-sm"
                                onClick={handleRemoverCupom}
                                aria-label="Remover cupom"
                              ></button>
                            </div>
                          )}
                          
                          {erroCupom && (
                            <div className="text-danger small mt-2">
                              <i className="fas fa-exclamation-circle me-1"></i>
                              {erroCupom}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Valor */}
                    <div className="text-center mb-4">
                      <h2 className="display-4 text-primary">
                        {cupomAplicado ? (
                          <>
                            <span className="text-muted text-decoration-line-through small d-block mb-2">
                              {frequencia === 'anual' 
                                ? formatarValor(plano.valor_anual)
                                : formatarValor(plano.valor_mensal)
                              }
                            </span>
                            {formatarValor(calcularValorComDesconto())}
                          </>
                        ) : (
                          <>
                            {frequencia === 'anual' 
                              ? formatarValor(plano.valor_anual)
                              : formatarValor(plano.valor_mensal)
                            }
                          </>
                        )}
                      </h2>
                      <p className="text-muted">
                        {frequencia === 'anual' ? 'por ano' : 'por mês'}
                      </p>
                      {/* Métodos aceitos (indicativo) */}
                      <div className="text-muted small">
                        <i className="fas fa-qrcode me-2"></i> PIX
                        <span className="mx-2">•</span>
                        <i className="fas fa-credit-card me-2"></i> Cartão
                        <span className="mx-2">•</span>
                        <i className="fas fa-barcode me-2"></i> Boleto
                      </div>
                      {frequencia === 'anual' && (
                        <p className="text-success">
                          <small>
                            Economize {formatarValor((plano.valor_mensal * 12) - plano.valor_anual)} por ano!
                          </small>
                        </p>
                      )}
                    </div>

                    {/* Recursos */}
                    <div className="mb-4">
                      <h5 className="text-center mb-3">Recursos Inclusos</h5>
                      <ul className="list-group">
                        {plano.recursos.pacientes_ilimitados && (
                          <li className="list-group-item">
                            <i className="fas fa-check text-success me-2"></i>
                            Pacientes Ilimitados
                          </li>
                        )}
                        {plano.recursos.usuarios_ilimitados && (
                          <li className="list-group-item">
                            <i className="fas fa-check text-success me-2"></i>
                            Usuários Ilimitados
                          </li>
                        )}
                        {plano.recursos.armazenamento_ilimitado && (
                          <li className="list-group-item">
                            <i className="fas fa-check text-success me-2"></i>
                            Armazenamento Ilimitado
                          </li>
                        )}
                        {plano.recursos.google_calendar && (
                          <li className="list-group-item">
                            <i className="fas fa-check text-success me-2"></i>
                            Integração Google Calendar
                          </li>
                        )}
                        {plano.recursos.email_automatico && (
                          <li className="list-group-item">
                            <i className="fas fa-check text-success me-2"></i>
                            Email Automático
                          </li>
                        )}
                        {plano.recursos.prontuarios_digitais && (
                          <li className="list-group-item">
                            <i className="fas fa-check text-success me-2"></i>
                            Prontuários Digitais (Criptografados)
                          </li>
                        )}
                        {plano.recursos.relatorios_completos && (
                          <li className="list-group-item">
                            <i className="fas fa-check text-success me-2"></i>
                            Relatórios Completos
                          </li>
                        )}
                        {plano.recursos.suporte_prioritario && (
                          <li className="list-group-item">
                            <i className="fas fa-check text-success me-2"></i>
                            Suporte Prioritário
                          </li>
                        )}
                      </ul>
                    </div>

                    {/* Período de Teste */}
                    {plano.dias_teste > 0 && !assinatura && (
                      <div className="alert alert-info text-center">
                        <i className="fas fa-gift me-2"></i>
                        <strong>{plano.dias_teste} dias de teste grátis</strong> ao se cadastrar!
                      </div>
                    )}

                    {/* Botão de Pagamento */}
                    {!mostrarBrick && (
                      <div className="text-center">
                        <button
                          type="button"
                          className="btn btn-primary btn-lg"
                          onClick={handleIniciarPagamento}
                        >
                          <i className="fas fa-credit-card me-2"></i>
                          {assinatura && assinatura.status_assinatura === 'ativo'
                            ? 'Renovar Plano'
                            : 'Contratar Plano PRO'
                          }
                        </button>
                        <div className="text-muted small mt-2">
                          Cancele quando quiser. Sem fidelidade.
                        </div>
                      </div>
                    )}

                    {/* Payment Brick */}
                    {mostrarBrick && plano && (
                      <div className="mt-4">
                        <h5 className="text-center mb-3">Complete o Pagamento</h5>
                        <MercadoPagoPaymentBrick
                          valor={calcularValorComDesconto()}
                          descricao={`Plano ${plano.nome} - ${frequencia === 'anual' ? 'Anual' : 'Mensal'}${cupomAplicado ? ` (Cupom ${cupomAplicado.codigo})` : ''}`}
                          onSuccess={handleSuccess}
                          onError={handleError}
                        />
                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setMostrarBrick(false)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ✅ Histórico de Transações - Movido para o final */}
          {transacoes.length > 0 && (
            <div className="row justify-content-center mt-4">
              <div className="col-12 col-md-10 col-lg-8 col-xl-6 mx-auto">
                <div className="card shadow-sm">
                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="fas fa-receipt me-2"></i>
                      Histórico de Pagamentos
                    </h3>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Data</th>
                            <th>Plano</th>
                            <th>Valor</th>
                            <th>Método</th>
                            <th>Status</th>
                            <th className="text-end">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transacoes.map((t) => (
                            <tr key={t.id}>
                              <td>
                                <small>{new Date(t.data_criacao).toLocaleDateString('pt-BR')}</small>
                              </td>
                              <td>
                                <small>{t.frequencia === 'mensal' ? 'Mensal' : 'Anual'}</small>
                              </td>
                              <td>
                                <small className="fw-bold">
                                  {t.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </small>
                              </td>
                              <td>
                                <small>
                                  {t.tipo_pagamento === 'bank_transfer' ? (
                                    <><i className="fas fa-qrcode text-info me-0"></i>{' '}PIX</>
                                  ) : t.tipo_pagamento === 'credit_card' ? (
                                    <><i className="fas fa-credit-card text-primary me-0"></i>{' '}Cartão</>
                                  ) : (
                                    t.metodo_pagamento
                                  )}
                                </small>
                              </td>
                              <td>
                                {t.status === 'approved' ? (
                                  <span className="badge bg-success">Aprovado</span>
                                ) : t.status === 'pending' ? (
                                  <span className="badge bg-warning">Pendente</span>
                                ) : t.status === 'rejected' ? (
                                  <span className="badge bg-danger">Rejeitado</span>
                                ) : (
                                  <span className="badge bg-secondary">{t.status}</span>
                              )}
                            </td>
                            <td className="text-end">
                              {/* Sem ações - atualização automática por webhook */}
                            </td>
                          </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>
    </>
  );
};
